import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import { isOpenerVariant, slotPackScale } from '@game-platform/pack-animation';
import type {
  ApiClient,
  IframePoolOrder,
  IframePoolOrderItem,
  VolatilityLevel,
} from '../api';
import { useOpeningAnimation } from '../openingAnimation';
import { useSkipAnimation } from './playerPreference';
import { ReadyStep } from './ReadyStep';
import { OpenerReadyStep } from './opener/OpenerReadyStep';
import { cardFactsFor } from './opener/cardFacts';
import { RevealBackgroundTint } from './RevealBackgroundTint';
import { RevealRays } from './RevealRays';
import { RevealGlowOverlay } from './RevealGlowOverlay';
import { slotPackScaleStyle } from './slotPackScale';
import { BadgeRevealSequence } from './BadgeRevealSequence';
import { WinnerStep } from './WinnerStep';
import { useRevealMachine } from './useRevealMachine';
import { useIsSleek } from '../theme';
import type { DemoRevealSource } from './demoSource';

export interface PackRevealProps {
  client: ApiClient;
  orderId: string;
  offer: IframePoolOrder['poolOffer'];
  item: IframePoolOrderItem;
  onRevealed: (updated: IframePoolOrderItem) => void;
  onBuyAgain: () => void;
  onShowPack: () => void;
  /** "Confirm Shipping" → open the delivery flow for this item. */
  onShip?: (item: IframePoolOrderItem) => void;
  /** Re-load the order from the backend. Used to recover from a 422
   *  on `mark_as_opened` (item already opened — fresh payload has the
   *  authoritative `opened_at` + `best_active_offer`). */
  refetchOrder: () => Promise<IframePoolOrder | null>;
  /** While this purchase still has a sealed pack ahead, the winner screen's
   *  "Open Next Pack" CTA advances to it (open an already-bought pack)
   *  instead of buying a brand-new one. */
  hasNextSealedPack?: boolean;
  /** Advance to the next still-sealed pack of this purchase. */
  onAdvanceToNextSealed?: () => void;
  /** Forwarded to WinnerStep — the last pack's CTA reads "Done". */
  onDone?: () => void;
  /** Bubbles up while this pack's winner screen is in an Open Next Pack
   *  loop or shuffle, so the host can lock pack navigation (a remount then
   *  would destroy the in-flight loop pack). */
  onLoopActiveChange?: (active: boolean) => void;
  /** Volatility the player selected — forwarded to "Open Next Pack". */
  volatility?: VolatilityLevel;
  /** Set = free demo rip. Same ceremony, same components; only the card's
   *  origin and the forward CTA change, and nothing is bought or sold.
   *  See `DemoRevealSource`. */
  demo?: DemoRevealSource;
}

/**
 * State machine for a single pack:
 *
 *   ready   → slot wheel spins → call mark_as_opened → winner
 *   winner  → show item + FMV + Buyback (if offer exists) + Next
 *
 * If the item was already opened before we landed (F5 into reveal after
 * a partially‑completed session), start directly in `winner`.
 *
 * Everything stateful lives in `useRevealMachine`; this component is a
 * shell that wires hook output into JSX and forwards events.
 */
export function PackReveal({
  client,
  orderId,
  offer,
  item,
  onRevealed,
  onBuyAgain,
  onShowPack,
  onShip,
  refetchOrder,
  hasNextSealedPack,
  onAdvanceToNextSealed,
  onDone,
  onLoopActiveChange,
  volatility,
  demo,
}: PackRevealProps) {
  const machine = useRevealMachine({
    client,
    orderId,
    item,
    offerId: offer.id,
    onRevealed,
    refetchOrder,
    ...(demo ? { demo } : {}),
  });

  // The pack's own opening mechanic, else the operator's. A library scene that
  // cannot run here (no WebGL, artwork refused) drops this pack back to the slot lane.
  const animation = useOpeningAnimation(offer.animation);
  const [openerFailed, setOpenerFailed] = useState(false);
  // The player would rather see the card than the ceremony: no scene and no
  // reel is mounted (a running scene is taken down), and the pack opens
  // straight to the winner, the way Skip on the slot lane does
  const skipping = useSkipAnimation();
  const useOpener = isOpenerVariant(animation.variant) && !openerFailed && !skipping;
  const skipToWinner = machine.onSkipReveal;
  useEffect(() => {
    if (!skipping || machine.status !== 'ready') return;
    void skipToWinner();
  }, [skipping, machine.status, skipToWinner]);
  // The scene reveals the card and then stays underneath the winner chrome —
  // the title and the bottom panel arrive around the card it settled, there
  // is no second card on a pedestal. A returning view (opened before this
  // mount) has nothing to open and gets the plain winner screen. Once the
  // winner starts its Open Next Pack loop the loop owns the stage (its own
  // scene or reel), so this scene goes and stays gone — should the loop be
  // aborted, the first pull comes back on a pedestal instead.
  const [loopStarted, setLoopStarted] = useState(false);
  // Sleek keeps the page dark after the pull, like its Vault card page: the
  // rarity shows as the glow behind the card, not as a wash over the screen.
  const isSleek = useIsSleek();
  const sceneBacked = useOpener && !machine.instant && !loopStarted;
  // The carousel's story, once the pull is known — one array per pull, so
  // the scene is not retuned on every render
  const current = machine.current;
  const facts = useMemo(
    () => (current.openedAt !== null ? cardFactsFor(current) : undefined),
    [current],
  );
  const handleLoopActiveChange = useCallback(
    (active: boolean) => {
      if (active) setLoopStarted(true);
      onLoopActiveChange?.(active);
    },
    [onLoopActiveChange],
  );

  return (
    // `contents`: no box of its own, so the layers below keep resolving
    // against the lane as before; it only hands the slot lane's pack size down.
    <div className="contents" style={slotPackScaleStyle(slotPackScale(animation))}>
      {/* Background tint — fades in after `mark_as_opened` returns
          a badge. Sits between the page gradient and the rays so
          the rays read on top. The base page background stays
          intact; we just wash a soft rarity-coloured halo over it.

          `fixed`, NOT `absolute` — same for the rays below. These two are
          BACKGROUND layers and have to reach the physical screen edges, and
          both are sized by their own box: the wash is a gradient on it, the
          rays crop to it. `absolute` resolves against the nearest positioned
          ancestor, which here is RevealScreen's `relative flex-1` lane, INSIDE
          the stage's `safe-area-pad` — so the wash stopped dead on the inset
          line and left the untinted base background showing as a band under
          the host's status bar, seam measured at exactly
          env(safe-area-inset-top) (177px = 59pt on a Dynamic Island phone).
          `fixed` anchors to the stage instead: OverlayHost's `@container`
          applies layout containment, which makes it the containing block for
          fixed descendants, and its padding box is the viewport. Its
          `overflow: hidden` keeps clipping these layers, so nothing new can
          escape the screen.

          The glow overlay below deliberately stays `absolute`: its plate is
          centred and scaled by a transform, and no ancestor between it and
          OverlayHost clips, so it already bleeds to the edges on its own.

          What would bring the seam back: a `transform` or `filter` landing on
          any wrapper between OverlayHost and these two — that steals the
          containing block. */}
      {machine.revealedBadge !== null && !isSleek && (
        <div className="pointer-events-none fixed inset-0 z-0">
          <RevealBackgroundTint badge={machine.revealedBadge} />
        </div>
      )}

      {/* Rotating rays — appear the moment the reveal phase starts,
          BEFORE the API answers. Always white; rarity is conveyed
          by the tint above + label inside the glow square. The
          `rotate` animation lives at PackReveal level so it doesn't
          reset on the ready→winner status swap. */}
      {machine.showRays && (
        <div className="pointer-events-none fixed inset-0 z-[1]">
          <RevealRays />
        </div>
      )}

      {/* Library mechanic: mounted for the whole pack — ready AND winner — so
          the card the scene settled stays put while the winner chrome comes
          in above it (same z level, later in the DOM). */}
      {sceneBacked && (
        <div className="absolute inset-0 z-10">
          <OpenerReadyStep
            offer={offer}
            animation={animation}
            cardImageUrl={machine.current.openedAt ? machine.current.imageUrl : null}
            badge={machine.revealedBadge}
            tier={machine.current.openedAt ? (machine.current.badge?.name ?? null) : null}
            {...(facts ? { facts } : {})}
            onOpened={machine.onOpenerOpened}
            onRevealed={machine.onOpenerRevealed}
            onError={() => setOpenerFailed(true)}
          />
        </div>
      )}

      <AnimatePresence mode="wait">
        {/* A skipped pack has no reel: it waits for the winner on the
            background alone */}
        {machine.status === 'ready' && !sceneBacked && !skipping && (
          <div key="ready" className="absolute inset-0 z-10">
            <ReadyStep
              offer={offer}
              onRevealStarted={machine.onRevealStarted}
              onExpansionStart={machine.onExpansionStart}
              onSkipToWinner={machine.onSkipReveal}
            />
          </div>
        )}
        {machine.status === 'winner' && (
          // `overflow-hidden` (was `overflow-y-auto`): the winner screen now
          // uses a fixed-region layout that always fits, and its entrance/exit
          // transforms (bottom fly-down) would otherwise make this wrapper
          // scrollable → a stray scrollbar mid-animation.
          <div key="winner" className="absolute inset-0 z-10 overflow-hidden">
            <WinnerStep
              client={client}
              item={machine.current}
              offer={offer}
              sellState={machine.sellState}
              onSell={machine.onSell}
              onBuyAgain={onBuyAgain}
              onShowPack={onShowPack}
              {...(onShip ? { onShip } : {})}
              hasNextSealedPack={hasNextSealedPack ?? false}
              {...(onAdvanceToNextSealed ? { onAdvanceToNextSealed } : {})}
              {...(onDone ? { onDone } : {})}
              onLoopActiveChange={handleLoopActiveChange}
              {...(volatility ? { volatility } : {})}
              instant={machine.instant}
              cardSlot={sceneBacked ? 'scene' : 'card'}
              {...(demo ? { demo } : {})}
            />
          </div>
        )}
      </AnimatePresence>

      {/* Glow overlay — single node owns BOTH the steady reveal-
          phase plate AND the scale-up wipe. Lives on PackReveal root
          (sibling of the AnimatePresence content) so it can bleed
          past the lane viewport's `overflow-hidden`. Phase machine:
          reveal (steady) → expand (scales up) → fade (opacity out). */}
      {machine.glowPhase !== null && (
        <RevealGlowOverlay
          phase={machine.glowPhase}
          badge={machine.revealedBadge}
          onExpandComplete={machine.onGlowExpanded}
          onFadeComplete={machine.onGlowFaded}
        />
      )}

      {/* Badge text sequence — character → grade. Lives above the
          glow (`z-40` vs glow's `z-30`) so the text reads on top of
          the expanded white pelt. `onComplete` releases the glow to
          fade, which then unmounts and reveals WinnerStep. */}
      {machine.showBadgeSequence && machine.current.badge && (
        <BadgeRevealSequence
          character={machine.current.badge.character}
          grade={machine.current.badge.grade}
          onComplete={machine.onBadgeSequenceDone}
        />
      )}
    </div>
  );
}
